import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Small copy-to-clipboard pill for phone numbers.
 *
 * Placed next to a DialButton so the user can quickly paste a number
 * into another dialer app (e.g. Heyo Phone) when the URL-scheme launch
 * doesn't work on their device.
 *
 * Usage:
 *   <span>{customer.phone} <DialButton number={customer.phone} /><CopyButton value={customer.phone} /></span>
 */
export default function CopyButton({ value, size = "xs", label = "Copy number", className = "" }) {
  const [copied, setCopied] = useState(false);

  const copy = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const v = String(value || "").trim();
    if (!v) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(v);
      } else {
        // Legacy fallback for older WebViews that don't expose Clipboard API.
        const ta = document.createElement("textarea");
        ta.value = v;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — long-press the number to copy manually.");
    }
  };

  const sizeCls =
    size === "xs" ? "w-6 h-6 p-0" : size === "lg" ? "h-9 px-3" : "w-7 h-7 p-0";
  const iconCls = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";

  return (
    <button
      type="button"
      onClick={copy}
      disabled={!value}
      className={`inline-flex items-center justify-center rounded-full border transition-colors disabled:opacity-50 ${
        copied
          ? "bg-emerald-600 border-emerald-600 text-white"
          : "bg-white border-slate-200 text-slate-500 hover:text-[#0055FF] hover:border-[#0055FF]"
      } ${sizeCls} ${className}`}
      title={label}
      aria-label={label}
      data-testid={`copy-btn-${String(value || "").replace(/(?!^\+)[^\d]/g, "")}`}
    >
      {copied ? <Check className={iconCls} /> : <Copy className={iconCls} />}
    </button>
  );
}
